/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState } from 'react';

// @mui material components
import Card from '@mui/material/Card';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';

// Material Dashboard 2 PRO React TS components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDAlert from 'components/MDAlert';

// Auth context
import { useAuth } from 'context/auth';

// react-router-dom components
import { useNavigate } from 'react-router-dom';

function DeleteAccount(): JSX.Element {
  const { deleteAccount } = useAuth();

  const [isDeactivateDialogOpen, setIsDeactivateDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const navigate = useNavigate();

  // const handleDeactivateClick = () => {
  //   setIsDeactivateDialogOpen(true);
  // };

  const handleDeleteClick = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsDeactivateDialogOpen(false);
    setIsDeleteDialogOpen(false);
    setPassword('');
    setError(null);
  };

  // const handleDeactivateConfirm = async () => {
  //   if (!password) {
  //     setError('Password is required');
  //     return;
  //   }

  //   setIsLoading(true);
  //   setError(null);

  //   try {
  //     await deactivateAccount(password);
  //     setSuccess('Your account has been deactivated successfully.');
  //     handleDialogClose();
  //     // Redirect will happen automatically due to logout in deactivateAccount
  //   } catch (err) {
  //     setError((err as Error).message);
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  const handleDeleteConfirm = async () => {
    if (!password) {
      setError('Password is required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await deleteAccount(password);
      setSuccess('Your account has been deleted successfully.');
      handleDialogClose();
      navigate('/');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card id="delete-account">
      <MDBox
        pr={3}
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        flexDirection={{ xs: 'column', sm: 'row' }}
      >
        <MDBox p={3} lineHeight={1}>
          <MDBox mb={1}>
            <MDTypography variant="h5">Delete Account</MDTypography>
          </MDBox>
          <MDTypography variant="button" color="text">
            Once you delete your account, there is no going back. Please be
            certain.
          </MDTypography>
        </MDBox>
        <MDBox display="flex" flexDirection={{ xs: 'column', sm: 'row' }}>
          {/* <MDButton
            variant="outlined"
            color="secondary"
            onClick={handleDeactivateClick}
          >
            deactivate
          </MDButton> */}
          <MDBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
            <MDButton
              variant="gradient"
              color="error"
              sx={{ height: '100%' }}
              onClick={handleDeleteClick}
            >
              delete account
            </MDButton>
          </MDBox>
        </MDBox>
      </MDBox>

      {success && (
        <MDBox p={3}>
          <MDAlert color="success">{success}</MDAlert>
        </MDBox>
      )}

      {/* Deactivate Account Dialog */}
      {/* <Dialog open={isDeactivateDialogOpen} onClose={handleDialogClose}>
        <DialogTitle>Deactivate Account</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your account will be deactivated, but your data will be preserved.
            You can reactivate your account by logging in again.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Enter your password to confirm"
            type="password"
            fullWidth
            variant="outlined"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={!!error}
            disabled={isLoading}
          />
          {error && (
            <MDTypography variant="caption" color="error">
              {error}
            </MDTypography>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={handleDialogClose}
            color="secondary"
            disabled={isLoading}
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={handleDeactivateConfirm}
            color="warning"
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={24} /> : 'Deactivate'}
          </MDButton>
        </DialogActions>
      </Dialog> */}

      {/* Delete Account Dialog */}
      <Dialog open={isDeleteDialogOpen} onClose={handleDialogClose}>
        <DialogTitle>Delete Account</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This action cannot be undone. All of your data will be permanently
            deleted.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Enter your password to confirm"
            type="password"
            fullWidth
            variant="outlined"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={!!error}
            disabled={isLoading}
          />
          {error && (
            <MDTypography variant="caption" color="error">
              {error}
            </MDTypography>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={handleDialogClose}
            color="secondary"
            disabled={isLoading}
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={handleDeleteConfirm}
            color="error"
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={24} /> : 'Delete Permanently'}
          </MDButton>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

export default DeleteAccount;
